import { HttpClient, HttpContext } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { timeout, map, catchError, of, forkJoin, filter } from 'rxjs';
import { SKIP_ERROR_INTERCEPTOR } from '../helpers/errors.interceptor';
import { ToastService } from '@atsystems/ui';

@Injectable({
  providedIn: 'root',
})
export class ServicesHealthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private context = new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true);
  private toastService = inject(ToastService);

  private _isChecking = signal(false);
  private _unavailableServices = signal<string[]>([]);

  public isChecking = this._isChecking.asReadonly();
  public unavailableServices = this._unavailableServices.asReadonly();
  public hasUnavailableServices = computed(() => this._unavailableServices().length > 0);

  private _previousRoute = signal<string | null>(null);
  public previousRoute = this._previousRoute.asReadonly();

  private _bypassedRoutes = signal<Set<string>>(new Set());

  constructor() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this._previousRoute.set(event.url);
      this.checkServicesForCurrentRoute();
    });
  }

  private checkServicesForCurrentRoute(): void {
    const currentRoute = this.getCurrentRouteWithData();
    const requiredServices = currentRoute?.snapshot?.data?.['services'] as string[];
    const currentUrl = this.router.url.split('?')[0];

    if (this._bypassedRoutes().has(currentUrl)) {
      this.clearBypass(currentUrl);
      return;
    }

    if (requiredServices && requiredServices.length > 0) {
      this.checkServices(requiredServices);
    }
  }

  private getCurrentRouteWithData(): any {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  public checkServices(serviceUrls: string[]): void {
    if (serviceUrls.length === 0) return;

    this._isChecking.set(true);
    this._unavailableServices.set([]);

    const healthChecks = serviceUrls.map(serviceUrl =>
      this.http.head(`${serviceUrl}/ping`, { context: this.context }).pipe(
        timeout(5000),
        map(() => ({ url: serviceUrl, available: true })),
        catchError(() => of({ url: serviceUrl, available: false }))
      )
    );

    forkJoin(healthChecks).subscribe(results => {
      this._isChecking.set(false);

      const unavailable = results.filter(r => !r.available).map(r => r.url);
      this._unavailableServices.set(unavailable);

      if (unavailable.length > 0) {
        setTimeout(() => {
          this.toastService.clear();
        }, 100);
        this.router.navigate(['/service-unavailable'], {
          state: {
            unavailableServices: unavailable,
            previousRoute: this._previousRoute(),
          },
        });
      }
    });
  }

  public bypassRoute(route: string): void {
    const current = new Set(this._bypassedRoutes());
    current.add(route);
    this._bypassedRoutes.set(current);
  }

  public clearBypass(route: string): void {
    const current = new Set(this._bypassedRoutes());
    current.delete(route);
    this._bypassedRoutes.set(current);
  }

  public clearUnavailableServices(): void {
    this._unavailableServices.set([]);
  }

  public retryPreviousRoute(): void {
    const previousRoute = this._previousRoute();
    if (previousRoute) {
      this.router.navigateByUrl(previousRoute);
    }
  }

  public forceNavigate(route: string): void {
    this.bypassRoute(route.split('?')[0]);
    this.router.navigateByUrl(route);
  }
}
